import { z } from "zod";
import { AppSettingsSchema } from "./settings.js";
import { RoleNoPasswordSchema } from "./permissions.js";
import { ListRoomsResponseSchema, RoomDescriptorSchema } from "./rooms.js";
import { RoomMediaKeyEpochEventSchema } from "./recording.js";
import { MaintenanceStatusSchema } from "./maintenance.js";
import { AppTypeSchema } from "./startup.js";
import { RoomFileAddedEventSchema, RoomFilesRemovedEventSchema, } from "./files.js";
const room = z.object({ roomId: z.string() });
const request = room.extend({ requestId: z.string() });
const event = (type, data) => z.object({ type: z.literal(type), data });
export const serverMessageSchemas = {
    pong: z.object({ type: z.literal("pong") }),
    maintenance: event("maintenance", MaintenanceStatusSchema),
    /** The instance's current app type (e.g. trial deadline): sent on connect and when it changes. */
    "app-type": event("app-type", AppTypeSchema),
    settings: event("settings", AppSettingsSchema),
    settings_added: event("settings_added", AppSettingsSchema),
    settings_modified: event("settings_modified", AppSettingsSchema),
    settings_removed: event("settings_removed", AppSettingsSchema.nullable()),
    permissions: event("permissions", RoleNoPasswordSchema),
    permissions_added: event("permissions_added", RoleNoPasswordSchema),
    permissions_modified: event("permissions_modified", RoleNoPasswordSchema),
    permissions_removed: event("permissions_removed", RoleNoPasswordSchema.nullable()),
    "rooms-snapshot": event("rooms-snapshot", ListRoomsResponseSchema),
    "room-added": event("room-added", RoomDescriptorSchema),
    "room-updated": event("room-updated", RoomDescriptorSchema),
    "room-closed": event("room-closed", room),
    "join-request": event("join-request", request.extend({ userId: z.string(), userName: z.string() })),
    "join-request-cancelled": event("join-request-cancelled", request),
    "join-request-resolved": event("join-request-resolved", request.extend({ userName: z.string(), granted: z.boolean() })),
    "join-approved": event("join-approved", request),
    "join-denied": event("join-denied", request),
    "join-request-expired": event("join-request-expired", request),
    // `transcription-messages` (live transcript lines) is retired: the
    // transcription bot sends them into the ODIN room itself, and clients
    // complete their copy from the backlog (`GET /v1/notifications/transcript`).
    /** Sent to each admitted member of the room only. */
    "room-media-key": event("room-media-key", RoomMediaKeyEpochEventSchema),
    /** Sent to each member of the room only. */
    "room-file-added": event("room-file-added", RoomFileAddedEventSchema),
    /** Sent to each member of the room only. */
    "room-files-removed": event("room-files-removed", RoomFilesRemovedEventSchema),
};
export const clientMessageSchemas = {
    ping: z.object({ type: z.literal("ping") }),
    "room-active": z.object({
        type: z.literal("room-active"),
        roomId: z.string().min(1),
    }),
    "room-rejoin": z.object({
        type: z.literal("room-rejoin"),
        roomId: z.string().min(1),
    }),
};
const { pong, ...serverDataMessages } = serverMessageSchemas;
export const ServerEventSchema = z.discriminatedUnion("type", [
    pong,
    ...Object.values(serverDataMessages),
]);
export const ClientEventSchema = z.discriminatedUnion("type", [
    clientMessageSchemas.ping,
    clientMessageSchemas["room-active"],
    clientMessageSchemas["room-rejoin"],
]);
/** Unknown future events and malformed known events are both ignored by receivers. */
export function parseServerEvent(value) {
    const parsed = ServerEventSchema.safeParse(value);
    return parsed.success ? parsed.data : undefined;
}
export function parseClientEvent(value) {
    const parsed = ClientEventSchema.safeParse(value);
    return parsed.success ? parsed.data : undefined;
}
